// =============================================================================
// SimSense Platform - Track Map Visualization
// =============================================================================
// When an SVG outline exists (from Track Images), ALL interactive elements
// (markers, hover, S/F, click) are positioned along the SVG path using the
// browser's native getPointAtLength() API with GPS-based start-offset
// alignment so that LapDistPct=0 maps to the actual start/finish line.
// =============================================================================

import { useLayoutEffect, useMemo, useRef, useState } from "react";

interface TrackMapProps {
  telemetry: any;
  telemetries?: any[];
  hoverLapDistPct: number | null;
  selectedLapColors: string[];
  width?: number | string;
  height?: number | string;
  trackModel?: any;
  onClickPct?: (pct: number) => void;
  zoomRange?: [number, number] | null; // Highlight zoomed section on track
}

type SvgPoint = { x: number; y: number };

type DisplayMarker = SvgPoint & {
  label: string;
  kind: "corner" | "stretch";
};

type OutlineMetrics = {
  minX: number;
  minY: number;
  width: number;
  height: number;
  totalLength: number;
};

// ─── Helpers ───────────────────────────────────────────────────────────────

function parseTurnNumber(value: unknown): number | null {
  if (typeof value !== "string") return null;
  const match = value.match(/(?:turn|t)\s*(\d+)/i) || value.match(/\b(\d+)\b/);
  return match ? Number(match[1]) : null;
}

function midpointPct(start: number, end: number): number {
  let wrappedEnd = end;
  if (wrappedEnd < start) wrappedEnd += 1;
  const mid = (start + wrappedEnd) / 2;
  return mid >= 1 ? mid - 1 : mid;
}

function normalizeLapDistPct(value: number): number {
  if (!Number.isFinite(value)) return 0;
  if (value > 1.5) return value / 100;
  if (value < 0) return 0;
  if (value >= 1) return value % 1;
  return value;
}

function buildTelemetryPolyline(telemetry: any, toSVG: (lat: number, lon: number) => SvgPoint): string {
  const lats = telemetry?.Lat as number[] | undefined;
  const lons = telemetry?.Lon as number[] | undefined;
  if (!lats || !lons) return "";
  const len = Math.min(lats.length, lons.length);
  const points: string[] = [];
  for (let i = 0; i < len; i++) {
    const lat = Number(lats[i]);
    const lon = Number(lons[i]);
    if (!Number.isFinite(lat) || !Number.isFinite(lon) || lat === 0 || lon === 0) continue;
    const point = toSVG(lat, lon);
    points.push(`${point.x.toFixed(2)},${point.y.toFixed(2)}`);
  }
  return points.join(" ");
}

function findPointAtPct(
  telemetry: any, targetPct: number, toSVG: (lat: number, lon: number) => SvgPoint,
): SvgPoint | null {
  const distPct = telemetry?.LapDistPct as number[] | undefined;
  const lats = telemetry?.Lat as number[] | undefined;
  const lons = telemetry?.Lon as number[] | undefined;
  if (!distPct || !lats || !lons) return null;

  let bestIdx = -1, bestDist = Infinity;
  for (let i = 0; i < distPct.length; i++) {
    const dist = Math.abs(normalizeLapDistPct(Number(distPct[i])) - targetPct);
    if (dist < bestDist) { bestDist = dist; bestIdx = i; }
  }
  if (bestIdx < 0) return null;
  const lat = Number(lats[bestIdx]);
  const lon = Number(lons[bestIdx]);
  if (!Number.isFinite(lat) || !Number.isFinite(lon) || lat === 0 || lon === 0) return null;
  return toSVG(lat, lon);
}

// ─── Component ─────────────────────────────────────────────────────────────

export function TrackMap({
  telemetry,
  telemetries = [],
  hoverLapDistPct,
  selectedLapColors,
  width = "100%",
  height = 460,
  trackModel,
  onClickPct,
  zoomRange,
}: TrackMapProps) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const outlineRef = useRef<SVGPathElement | null>(null);
  const hoverRafRef = useRef<number | null>(null); // Throttle hover to animation frames
  const [outlineMetrics, setOutlineMetrics] = useState<OutlineMetrics | null>(null);
  const outlinePath = trackModel?.svgOutline || trackModel?.svg_outline || null;

  // ── Measure SVG outline path via DOM ───────────────────────────────────
  useLayoutEffect(() => {
    if (!outlinePath || !outlineRef.current) {
      setOutlineMetrics(null);
      return;
    }
    try {
      const bbox = outlineRef.current.getBBox();
      const totalLength = outlineRef.current.getTotalLength();
      if (!Number.isFinite(bbox.width) || !Number.isFinite(bbox.height) ||
          bbox.width <= 0 || bbox.height <= 0 ||
          !Number.isFinite(totalLength) || totalLength <= 0) {
        setOutlineMetrics(null);
        return;
      }
      setOutlineMetrics({
        minX: bbox.x, minY: bbox.y,
        width: bbox.width, height: bbox.height,
        totalLength,
      });
    } catch {
      setOutlineMetrics(null);
    }
  }, [outlinePath]);

  // ── GPS-based geometry (fallback when no SVG outline) ──────────────────
  const geo = useMemo(() => {
    if (!telemetry?.Lat || !telemetry?.Lon) return null;
    const lats = telemetry.Lat as number[];
    const lons = telemetry.Lon as number[];
    const len = Math.min(lats.length, lons.length);
    if (len === 0) return null;

    let minLat = Infinity, maxLat = -Infinity;
    let minLon = Infinity, maxLon = -Infinity;
    for (let i = 0; i < len; i++) {
      const lat = Number(lats[i]);
      const lon = Number(lons[i]);
      if (!Number.isFinite(lat) || !Number.isFinite(lon) || lat === 0 || lon === 0) continue;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
      if (lon < minLon) minLon = lon;
      if (lon > maxLon) maxLon = lon;
    }
    if (!Number.isFinite(minLat) || !Number.isFinite(minLon)) return null;

    const viewWidth = 400;
    const viewHeight = 400;
    const padding = 40;
    const latRange = maxLat - minLat || 1e-6;
    const lonRange = maxLon - minLon || 1e-6;
    const scale = Math.min(
      (viewWidth - padding * 2) / lonRange,
      (viewHeight - padding * 2) / latRange,
    );
    const xCenter = viewWidth / 2 - ((minLon + maxLon) / 2) * scale;
    const yCenter = viewHeight / 2 + ((minLat + maxLat) / 2) * scale;
    const toSVG = (lat: number, lon: number) => ({
      x: lon * scale + xCenter,
      y: -lat * scale + yCenter,
    });
    return { svgPoints: buildTelemetryPolyline(telemetry, toSVG), toSVG, viewWidth, viewHeight };
  }, [telemetry]);

  // ── Start-offset alignment via CURVATURE cross-correlation ──────────────
  // Computes curvature profiles for both the SVG path and GPS track, then
  // cross-correlates them. Curvature is coordinate-system independent —
  // it doesn't need GPS-to-SVG mapping, just the shape signature (turns=high,
  // straights=low). This finds the correct offset and direction robustly.
  const pathStartOffset = useMemo<{ offset: number; reverse: boolean }>(() => {
    if (!outlineMetrics || !outlineRef.current ||
        !telemetry?.Lat || !telemetry?.Lon || !telemetry?.LapDistPct) {
      return { offset: 0, reverse: false };
    }

    const lats = telemetry.Lat as number[];
    const lons = telemetry.Lon as number[];
    const distPct = telemetry.LapDistPct as number[];
    if (lats.length < 20) return { offset: 0, reverse: false };

    const path = outlineRef.current!;
    const totalLen = outlineMetrics.totalLength;
    const N = 200; // sample resolution

    // Helper: compute curvature at each sample (|sin(angle)| between consecutive segments)
    const computeCurvature = (pts: SvgPoint[]): number[] => {
      const k: number[] = [];
      for (let i = 0; i < pts.length; i++) {
        const prev = pts[(i - 1 + pts.length) % pts.length];
        const curr = pts[i];
        const next = pts[(i + 1) % pts.length];
        const dx1 = curr.x - prev.x, dy1 = curr.y - prev.y;
        const dx2 = next.x - curr.x, dy2 = next.y - curr.y;
        const cross = Math.abs(dx1 * dy2 - dy1 * dx2);
        const denom = Math.sqrt(dx1 * dx1 + dy1 * dy1) * Math.sqrt(dx2 * dx2 + dy2 * dy2);
        k.push(denom > 1e-10 ? cross / denom : 0);
      }
      // Smooth with moving average (window = N/20)
      const w = Math.max(3, Math.floor(N / 20));
      return k.map((_, i) => {
        let sum = 0;
        for (let j = -w; j <= w; j++) sum += k[(i + j + k.length) % k.length];
        return sum / (2 * w + 1);
      });
    };

    // Step 1: Curvature of SVG path
    const svgPts: SvgPoint[] = [];
    for (let i = 0; i < N; i++) {
      const pt = path.getPointAtLength((totalLen * i) / N);
      svgPts.push({ x: pt.x, y: pt.y });
    }
    const svgK = computeCurvature(svgPts);

    // Step 2: Curvature of GPS track (sampled at N evenly-spaced LapDistPct values)
    const gpsPts: SvgPoint[] = [];
    for (let s = 0; s < N; s++) {
      const targetPct = s / N;
      let bestIdx = 0, bestD = Infinity;
      for (let i = 0; i < distPct.length; i++) {
        const d = Math.abs(normalizeLapDistPct(Number(distPct[i])) - targetPct);
        if (d < bestD && Number.isFinite(Number(lats[i])) && Number(lats[i]) !== 0) {
          bestD = d; bestIdx = i;
        }
      }
      gpsPts.push({ x: Number(lons[bestIdx]), y: Number(lats[bestIdx]) });
    }
    const gpsK = computeCurvature(gpsPts);

    // Step 3: Cross-correlate curvature signals (dot product for each offset)
    let bestOffset = 0, bestReverse = false, bestCorr = -Infinity;
    for (let o = 0; o < N; o++) {
      let fwdCorr = 0, revCorr = 0;
      for (let i = 0; i < N; i++) {
        fwdCorr += svgK[(i + o) % N] * gpsK[i];
        revCorr += svgK[(N - i + o) % N] * gpsK[i];
      }
      if (fwdCorr > bestCorr) { bestCorr = fwdCorr; bestOffset = o; bestReverse = false; }
      if (revCorr > bestCorr) { bestCorr = revCorr; bestOffset = o; bestReverse = true; }
    }

    return { offset: bestOffset / N, reverse: bestReverse };
  }, [outlineMetrics, telemetry]);

  // ── Unified point finder ───────────────────────────────────────────────
  const getMapPointAtPct = (targetPct: number): SvgPoint | null => {
    if (outlinePath) {
      if (!outlineMetrics || !outlineRef.current) return null;
      const normalizedPct = normalizeLapDistPct(targetPct);
      const frac = pathStartOffset.reverse
        ? ((pathStartOffset.offset - normalizedPct + 1) % 1)
        : ((normalizedPct + pathStartOffset.offset) % 1);
      const point = outlineRef.current.getPointAtLength(outlineMetrics.totalLength * frac);
      return { x: point.x, y: point.y };
    }
    if (!geo) return null;
    return findPointAtPct(telemetry, targetPct, geo.toSVG);
  };

  // ── SVG click → nearest point on path → LapDistPct ────────────────────
  const getLocalSvgPoint = (clientX: number, clientY: number): SvgPoint | null => {
    if (!svgRef.current) return null;
    const ctm = svgRef.current.getScreenCTM();
    if (!ctm) return null;
    const point = svgRef.current.createSVGPoint();
    point.x = clientX;
    point.y = clientY;
    const localPoint = point.matrixTransform(ctm.inverse());
    return { x: localPoint.x, y: localPoint.y };
  };

  // Pre-compute path samples for fast nearest-point lookups (avoids getPointAtLength per mouse move)
  const pathSamples = useMemo(() => {
    if (!outlineMetrics || !outlineRef.current) return null;
    const path = outlineRef.current;
    const totalLen = outlineMetrics.totalLength;
    const N = 500;
    const samples: SvgPoint[] = [];
    for (let i = 0; i < N; i++) {
      const pt = path.getPointAtLength((totalLen * i) / N);
      samples.push({ x: pt.x, y: pt.y });
    }
    return samples;
  }, [outlineMetrics]);

  const findNearestOutlinePct = (point: SvgPoint): number | null => {
    if (!pathSamples || !outlineMetrics) return null;
    const N = pathSamples.length;
    // Fast array lookup instead of getPointAtLength per call
    let bestIdx = 0, bestDist = Infinity;
    for (let i = 0; i < N; i++) {
      const dx = pathSamples[i].x - point.x;
      const dy = pathSamples[i].y - point.y;
      const d = dx * dx + dy * dy;
      if (d < bestDist) { bestDist = d; bestIdx = i; }
    }
    // Convert path fraction to LapDistPct
    const pathFrac = bestIdx / N;
    let lapPct: number;
    if (pathStartOffset.reverse) {
      lapPct = (pathStartOffset.offset - pathFrac + 1) % 1;
    } else {
      lapPct = (pathFrac - pathStartOffset.offset + 1) % 1;
    }
    return lapPct;
  };

  // ── ViewBox: auto-fit to SVG outline when available ────────────────────
  const viewBox = useMemo(() => {
    if (outlineMetrics) {
      const padding = Math.max(Math.min(outlineMetrics.width, outlineMetrics.height) * 0.12, 24);
      return {
        minX: outlineMetrics.minX - padding,
        minY: outlineMetrics.minY - padding,
        width: outlineMetrics.width + padding * 2,
        height: outlineMetrics.height + padding * 2,
      };
    }
    if (geo) {
      return { minX: 0, minY: 0, width: geo.viewWidth, height: geo.viewHeight };
    }
    return { minX: 0, minY: 0, width: 400, height: 400 };
  }, [geo, outlineMetrics]);

  // ── Rotation: straighten the track by aligning the principal axis ──────
  const rotationDeg = useMemo(() => {
    if (!outlineMetrics || !outlineRef.current) return 0;
    const path = outlineRef.current;
    const totalLen = outlineMetrics.totalLength;
    const N = 100;
    // Sample path points
    let cx = 0, cy = 0;
    const pts: SvgPoint[] = [];
    for (let i = 0; i < N; i++) {
      const pt = path.getPointAtLength((totalLen * i) / N);
      pts.push({ x: pt.x, y: pt.y });
      cx += pt.x; cy += pt.y;
    }
    cx /= N; cy /= N;
    // PCA: compute covariance and principal axis angle
    let sxx = 0, syy = 0, sxy = 0;
    for (const p of pts) {
      const dx = p.x - cx, dy = p.y - cy;
      sxx += dx * dx; syy += dy * dy; sxy += dx * dy;
    }
    const angle = Math.atan2(2 * sxy, sxx - syy) / 2;
    return -(angle * 180 / Math.PI); // Negate to correct the tilt
  }, [outlineMetrics]);

  // ── Zoom highlight: build a sub-path for the zoomed section ────────────
  const zoomHighlightPath = useMemo(() => {
    if (!zoomRange || !outlineMetrics || !outlineRef.current || !pathStartOffset) return null;
    const [zMin, zMax] = zoomRange;
    if (zMax - zMin >= 0.99) return null; // Full range, no highlight needed
    const path = outlineRef.current;
    const totalLen = outlineMetrics.totalLength;
    const steps = 60;
    const points: string[] = [];
    for (let i = 0; i <= steps; i++) {
      const pct = zMin + (zMax - zMin) * (i / steps);
      const frac = pathStartOffset.reverse
        ? ((pathStartOffset.offset - pct + 1) % 1)
        : ((pct + pathStartOffset.offset) % 1);
      const pt = path.getPointAtLength(totalLen * frac);
      points.push(`${pt.x.toFixed(1)},${pt.y.toFixed(1)}`);
    }
    return points.join(" ");
  }, [zoomRange, outlineMetrics, pathStartOffset]);

  // ── Lap polylines (GPS fallback only) ──────────────────────────────────
  const overlayTelemetries = telemetries.length > 0 ? telemetries : [telemetry];

  const lapPolylines = useMemo(() => {
    if (!geo) return [];
    return overlayTelemetries
      .map((lap, index) => ({
        color: selectedLapColors[index] || selectedLapColors[selectedLapColors.length - 1] || "var(--accent)",
        points: buildTelemetryPolyline(lap, geo.toSVG),
      }))
      .filter((entry) => entry.points.length > 0);
  }, [geo, overlayTelemetries, selectedLapColors]);

  // ── Markers ────────────────────────────────────────────────────────────
  const fallbackMarkers = useMemo<DisplayMarker[]>(() => {
    if (!trackModel?.corners) return [];
    return (trackModel.corners as any[])
      .map((corner) => {
        const entryPct = corner.entry_pct ?? corner.start_pct ?? 0;
        const exitPct = corner.exit_pct ?? corner.end_pct ?? 0;
        const point = getMapPointAtPct(midpointPct(entryPct, exitPct));
        if (!point) return null;
        return { ...point, label: corner.name || corner.id || "Corner", kind: "corner" as const };
      })
      .filter(Boolean) as DisplayMarker[];
  }, [geo, outlineMetrics, outlinePath, pathStartOffset, telemetry, trackModel]);

  const displayMarkers = useMemo<DisplayMarker[]>(() => {
    if (!trackModel?.corners) return fallbackMarkers;
    const corners = [...trackModel.corners].sort(
      (a: any, b: any) => (a.entry_pct ?? a.start_pct ?? 0) - (b.entry_pct ?? b.start_pct ?? 0),
    );
    const turnMap = new Map<number, any>();
    for (const corner of corners) {
      const turnNumber = parseTurnNumber(corner.name ?? corner.id);
      if (turnNumber !== null && !turnMap.has(turnNumber)) turnMap.set(turnNumber, corner);
    }

    const turn1 = turnMap.get(1), turn2 = turnMap.get(2), turn3 = turnMap.get(3), turn4 = turnMap.get(4);
    if (!turn1 || !turn2 || !turn3 || !turn4) return fallbackMarkers;

    const markerSpecs = [
      { label: "Turns 1 & 2", kind: "corner" as const, pct: midpointPct(turn1.entry_pct ?? turn1.start_pct ?? 0, turn2.exit_pct ?? turn2.end_pct ?? 0) },
      { label: "Turns 3 & 4", kind: "corner" as const, pct: midpointPct(turn3.entry_pct ?? turn3.start_pct ?? 0, turn4.exit_pct ?? turn4.end_pct ?? 0) },
      { label: "Back Stretch", kind: "stretch" as const, pct: midpointPct(turn2.exit_pct ?? turn2.end_pct ?? 0, turn3.entry_pct ?? turn3.start_pct ?? 0) },
      { label: "Front Stretch", kind: "stretch" as const, pct: midpointPct(turn4.exit_pct ?? turn4.end_pct ?? 0, turn1.entry_pct ?? turn1.start_pct ?? 0) },
    ];
    const markers = markerSpecs
      .map((spec) => {
        const point = getMapPointAtPct(spec.pct);
        if (!point) return null;
        return { ...point, label: spec.label, kind: spec.kind };
      })
      .filter(Boolean) as DisplayMarker[];
    return markers.length > 0 ? markers : fallbackMarkers;
  }, [fallbackMarkers, geo, outlineMetrics, outlinePath, pathStartOffset, telemetry, trackModel]);

  // S/F position + perpendicular direction for the finish line
  const startFinish = useMemo(() => {
    const pos = getMapPointAtPct(0);
    if (!pos) return null;
    // Compute tangent at S/F by sampling nearby points
    const p1 = getMapPointAtPct(0.998);
    const p2 = getMapPointAtPct(0.002);
    if (!p1 || !p2) return { ...pos, perpX: 0, perpY: -1 }; // Fallback vertical
    const dx = p2.x - p1.x, dy = p2.y - p1.y;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    // Perpendicular (rotated 90°): (-dy, dx) normalized
    return { ...pos, perpX: (-dy / len) * 14, perpY: (dx / len) * 14 };
  }, [geo, outlineMetrics, outlinePath, pathStartOffset, telemetry]);

  const hoverPos = useMemo(() => {
    if (hoverLapDistPct === null) return null;
    return getMapPointAtPct(hoverLapDistPct);
  }, [geo, hoverLapDistPct, outlineMetrics, outlinePath, pathStartOffset, telemetry]);

  // ── Render ─────────────────────────────────────────────────────────────
  if (!geo && !outlinePath) {
    return (
      <div style={{ width, height, background: "var(--card)", borderRadius: "8px", border: "1px solid var(--border)", padding: "16px", display: "flex", flexDirection: "column" }}>
        <div style={{ fontSize: "10px", fontWeight: 700, textTransform: "uppercase", color: "var(--text3)", marginBottom: "12px", letterSpacing: "0.5px" }}>Track Position</div>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", gap: "8px" }}>
          <div style={{ fontSize: "12px", color: "var(--text2)", fontWeight: 600 }}>Map Geometry Unavailable</div>
          <div style={{ fontSize: "10px", color: "var(--text3)", maxWidth: "200px", lineHeight: 1.4 }}>
            This session lacks GPS data. Reprocess this session in <strong>System Administration</strong> to enable the track map.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ width, height, background: "var(--card)", borderRadius: "8px", border: "1px solid var(--border)", padding: "16px", display: "flex", flexDirection: "column" }}>
      <div style={{ fontSize: "10px", fontWeight: 700, textTransform: "uppercase", color: "var(--text3)", marginBottom: "12px", letterSpacing: "0.5px" }}>Track Position</div>
      <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
        <svg
          ref={svgRef}
          width="100%"
          height="100%"
          viewBox={`${viewBox.minX} ${viewBox.minY} ${viewBox.width} ${viewBox.height}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ display: "block", cursor: "default", transform: rotationDeg ? `rotate(${rotationDeg}deg)` : undefined, transformOrigin: "center center" }}
          onMouseMove={(e) => {
            if (!onClickPct) return;
            // Throttle to animation frames to prevent excessive event dispatching
            const clientX = e.clientX, clientY = e.clientY;
            if (hoverRafRef.current !== null) return;
            hoverRafRef.current = requestAnimationFrame(() => {
              hoverRafRef.current = null;
              const localPoint = getLocalSvgPoint(clientX, clientY);
              if (!localPoint) return;

              if (outlinePath) {
                const pct = findNearestOutlinePct(localPoint);
                if (pct !== null) onClickPct(pct);
                return;
              }

              if (!geo || !telemetry?.LapDistPct || !telemetry?.Lat || !telemetry?.Lon) return;
              let bestIdx = -1, bestDist = Infinity;
              const lats = telemetry.Lat as number[];
              const lons = telemetry.Lon as number[];
              for (let i = 0; i < Math.min(lats.length, lons.length); i++) {
                const point = geo.toSVG(lats[i], lons[i]);
                const dist = (point.x - localPoint.x) ** 2 + (point.y - localPoint.y) ** 2;
                if (dist < bestDist) { bestDist = dist; bestIdx = i; }
              }
              if (bestIdx >= 0) {
                onClickPct(normalizeLapDistPct(Number(telemetry.LapDistPct[bestIdx])));
              }
            });
          }}
        >
          {/* SVG outline: hidden measurement path + visible strokes */}
          {outlinePath ? (
            <>
              <path ref={outlineRef} d={outlinePath} fill="none" stroke="transparent" strokeWidth="1" pointerEvents="none" />
              {/* Wide invisible hit area for hover detection */}
              <path d={outlinePath} fill="none" stroke="transparent" strokeWidth="30" pointerEvents="stroke" />
              <path d={outlinePath} fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="10" strokeLinejoin="round" strokeLinecap="round" />
              <path d={outlinePath} fill="none" stroke="var(--accent)" strokeWidth="3" strokeOpacity="0.6" strokeLinejoin="round" strokeLinecap="round" />
            </>
          ) : geo?.svgPoints ? (
            <>
              <polyline points={geo.svgPoints} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="8" strokeLinejoin="round" strokeLinecap="round" />
              <polyline points={geo.svgPoints} fill="none" stroke="var(--accent)" strokeWidth="3" strokeOpacity="0.45" strokeLinejoin="round" strokeLinecap="round" />
            </>
          ) : null}

          {/* GPS lap overlays — only when no SVG outline (different coordinate space) */}
          {!outlinePath && lapPolylines.map((lap, index) => (
            <polyline
              key={index}
              points={lap.points}
              fill="none"
              stroke={lap.color}
              strokeWidth={index === 0 ? 2.4 : 1.5}
              strokeOpacity={index === 0 ? 0.78 : 0.34}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          ))}

          {/* Zoom highlight on track — light red to contrast with blue track */}
          {zoomHighlightPath && (
            <polyline
              points={zoomHighlightPath}
              fill="none"
              stroke="rgba(239, 68, 68, 0.6)"
              strokeWidth="12"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          )}

          {/* Corner/stretch labels (no dots — just text) */}
          {displayMarkers.map((marker, index) => (
            <g key={`${marker.label}-${index}`}>
              <text
                x={marker.x}
                y={marker.y - 8}
                textAnchor="middle"
                fill={marker.kind === "stretch" ? "var(--text1)" : "var(--text2)"}
                fontSize="12"
                fontWeight="700"
                fontFamily="Outfit, sans-serif"
              >
                {marker.label}
              </text>
            </g>
          ))}

          {/* Start/Finish — red line perpendicular to track direction */}
          {startFinish && (
            <line
              x1={startFinish.x - startFinish.perpX}
              y1={startFinish.y - startFinish.perpY}
              x2={startFinish.x + startFinish.perpX}
              y2={startFinish.y + startFinish.perpY}
              stroke="#ef4444"
              strokeWidth="3"
              strokeLinecap="round"
            />
          )}

          {/* Hover position dot */}
          {hoverPos && (
            <circle
              cx={hoverPos.x}
              cy={hoverPos.y}
              r="7"
              fill={selectedLapColors[0] || "var(--accent)"}
              stroke="rgba(255,255,255,0.9)"
              strokeWidth="2"
              filter="drop-shadow(0 0 6px rgba(255,255,255,0.4))"
            />
          )}
        </svg>
      </div>
    </div>
  );
}
